import { mainAccords } from './brand-landing.js';

export function cataloguePageExists(page: { page: number; total: number; pageSize: number }) {
  return page.page <= Math.max(1, Math.ceil(page.total / page.pageSize));
}

export function hasCloseDupe(detail: { alternatives?: readonly unknown[] }) {
  return detail.alternatives?.some(
    relationship =>
      typeof relationship === 'object' &&
      relationship !== null &&
      'type' in relationship &&
      relationship.type === 'CLOSE_DUPE',
  ) ?? false;
}

// Every published fragrance has its own profile, including those awaiting comparisons.
export function comparisonPageIndexable(_detail: {
  isOriginal: boolean;
  originals?: unknown[];
  alternatives?: unknown[];
}) {
  return true;
}

// One comparison has one address, reached from either fragrance, so the original always leads the
// path however the visitor arrived at it.
export function comparisonPath(originalSlug: string, alternativeSlug: string) {
  return '/compare/' + encodeURIComponent(originalSlug) + '/' + encodeURIComponent(alternativeSlug);
}

/** A pair derived from a shared comparison has one stable address from either fragrance. */
export function siblingComparisonPath(firstSlug: string, secondSlug: string) {
  const [first, second] = [firstSlug, secondSlug].sort();
  return comparisonPath(first!, second!);
}

/** The two slugs in a /compare path, or null when it is not one. */
export function parseComparisonPath(pathname: string) {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts.length !== 3 || parts[0] !== 'compare') return null;
  try {
    const original = decodeURIComponent(parts[1]!), alternative = decodeURIComponent(parts[2]!);
    return original && alternative ? { original, alternative } : null;
  } catch {
    return null;
  }
}

// Names built around another fragrance ("Inspired by …", numbered house codes, dupe or clone wording).
export function isReferenceNamed(name: string) {
  return /\binspired by\b|\bdupe\b|\bclone\b|\bsmells like\b|\s-\s\d{3,4}\b/i.test(name);
}

type ComparedFragrance = { name: string; scentProfile?: unknown };
/** Accords recorded for both fragrances, case-insensitively. */
export function sharedRecordedAccords(first: ComparedFragrance, second: ComparedFragrance) {
  const other = new Set(mainAccords(second.scentProfile, 12).map(accord => accord.name.toLocaleLowerCase('en')));
  return mainAccords(first.scentProfile, 12).filter(accord => other.has(accord.name.toLocaleLowerCase('en')));
}

// Search indexing for comparison pages (owner decision, 6 October 2026: the middle path). Only factual,
// editor-curated pairs are offered to search: a published relationship, direct or between two alternatives
// that share an original (never a pair a visitor chose, which has no limit), neither name built around another fragrance, at least three
// recorded accords on each side and at least two in common. Every other comparison stays noindex, follow.
export function comparisonSearchIndexable(comparison: { original: ComparedFragrance; alternative: ComparedFragrance; editorial?: boolean }) {
  const { original, alternative } = comparison;
  if (!comparison.editorial) return false;
  if (isReferenceNamed(original.name) || isReferenceNamed(alternative.name)) return false;
  if (mainAccords(original.scentProfile, 12).length < 3 || mainAccords(alternative.scentProfile, 12).length < 3) return false;
  return sharedRecordedAccords(original, alternative).length >= 2;
}
